import * as React from 'react';
import { Avatar, AvatarImage, AvatarFallback } from './avatar';
import { cn } from '../../lib/utils';

export interface UserAvatarProps {
  /** Image URL. Falls back to initials when null/empty. */
  src?: string | null;
  /** Full display name — used for alt text and initials derivation. */
  name: string;
  /** Extra classes applied to the root Avatar element. Controls size, border, position, etc. */
  className?: string;
}

function deriveInitials(name: string): string {
  const parts = name.trim().split(/\s+/);
  if (parts.length >= 2) return (parts[0]![0]! + parts[parts.length - 1]![0]!).toUpperCase();
  return (name[0] ?? 'U').toUpperCase();
}

/**
 * A single-prop avatar for user entities. Wraps the shadcn Avatar primitive
 * with standard image + initials-fallback behaviour so consumers never need
 * to compose Avatar/AvatarImage/AvatarFallback manually.
 */
export const UserAvatar = React.forwardRef<HTMLSpanElement, UserAvatarProps>(
  ({ src, name, className }, ref) => (
    <Avatar ref={ref} className={cn('shrink-0', className)}>
      {src && <AvatarImage src={src} alt={name} />}
      <AvatarFallback>{deriveInitials(name)}</AvatarFallback>
    </Avatar>
  ),
);
UserAvatar.displayName = 'UserAvatar';
